// Axe « plancher -> cible » d'un compte, partagé par la card de la liste et
// l'en-tête de la page de détails. C'est lui qui porte la continuité visuelle
// entre les deux pages.
//
// La GÉOMÉTRIE vit dans `accountProgressGeometry.ts` (module pur, testé sans
// React) ; ce composant ne fait que la rendre. Aucune couleur en dur : les 4
// thèmes (dark, light, jungle, sunrise) doivent rester lisibles.
//
// ⚠️ COULEURS — piège vérifié : les variables CSS s'appellent
// `--edgely-pnl-positive` / `--edgely-pnl-negative` (Main.css), PAS
// `--pnl-positive`. Elles portent un triplet HSL nu, donc `var(--x)` seul ne
// produit AUCUNE couleur valide (élément invisible, sans erreur). On passe
// donc par les classes Tailwind `pnlPositive` / `pnlNegative` / `warning`
// (tailwind.config.js), qui font déjà le `hsl(var(--...))` — c'est l'idiome
// du reste du dossier (AccountTable.tsx, PropFirmCalendar.tsx).

import useTranslation from "../../client/hooks/useTranslation";
import { buildAxisGeometry, type AxisBounds } from "../accountProgressGeometry";

export type AxisTone = "normal" | "warning" | "violation";

function formatMoney(value: number, currency: string): string {
  // `Intl.NumberFormat` rend "NaN" sur une valeur non finie. Le module de
  // géométrie laisse DÉLIBÉRÉMENT passer une balance non finie (une valeur
  // maquillée en 0 mentirait sur le solde) : le garde vit donc ici, au point
  // de rendu, comme ailleurs dans ce dossier (cf. RuleUpdateBanner.tsx).
  if (!Number.isFinite(value)) return "—";
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function AccountProgressBar({
  bounds,
  currentBalance,
  currency,
  tone = "normal",
  size = "sm",
}: {
  bounds: AxisBounds;
  currentBalance: number;
  currency: string;
  tone?: AxisTone;
  /** `sm` sur la card, `lg` en tête de la page de détails. */
  size?: "sm" | "lg";
}) {
  const t = useTranslation();
  const g = buildAxisGeometry(bounds, currentBalance);

  // Le remplissage porte l'ÉTAT. En marche normale il suit le signe du PnL ;
  // une alerte l'emporte sur le signe, parce qu'un compte en profit peut
  // avoir entamé sa marge.
  const fillClass =
    tone === "violation"
      ? "bg-pnlNegative"
      : tone === "warning"
        ? "bg-warning"
        : g.isPositive
          ? "bg-pnlPositive"
          : "bg-pnlNegative";

  const trackHeight = size === "lg" ? "h-2.5" : "h-1.5";
  const valueSize = size === "lg" ? "text-lg" : "text-sm";

  return (
    <div className="space-y-2">
      {/* Repères de bornes */}
      <div className="flex items-baseline justify-between gap-2 text-[10px] uppercase tracking-wide">
        <span
          className="font-mono text-pnlNegative"
          title={t.tradingAccounts.axisFloorHint}
        >
          {formatMoney(bounds.floor, currency)}{" "}
          <span className="text-muted-foreground">
            {t.tradingAccounts.axisFloor}
          </span>
        </span>
        <span className="font-mono text-pnlPositive">
          {formatMoney(bounds.target, currency)}{" "}
          <span className="text-muted-foreground">
            {/* Un PIC n'est pas un objectif. Sur un compte financé sans seuil
                de retrait chiffré, la borne droite est le pic historique
                (cf. `targetKind` dans accountProgressGeometry.ts) : le
                libeller « Cible » affirmerait un but que la prop firm n'a
                jamais fixé. */}
            {bounds.targetKind === "peak"
              ? t.tradingAccounts.axisPeak
              : t.tradingAccounts.axisTarget}
          </span>
        </span>
      </div>

      {/* L'axe */}
      <div className={`relative w-full rounded-full bg-muted ${trackHeight}`}>
        <div
          className={`absolute inset-y-0 rounded-full transition-all duration-500 ${fillClass}`}
          style={{
            left: `${g.fillStartRatio * 100}%`,
            width: `${Math.max(0, g.fillEndRatio - g.fillStartRatio) * 100}%`,
          }}
        />
        {/* Repère de départ */}
        <div
          className="absolute -top-1 -bottom-1 w-0.5 rounded-full bg-foreground/50"
          style={{ left: `${g.startRatio * 100}%` }}
          title={t.tradingAccounts.axisStart}
        />
      </div>

      {/* Les trois valeurs */}
      <div className="flex items-end justify-between gap-2">
        <div className="min-w-0">
          <p className={`font-mono font-semibold ${valueSize}`}>
            {formatMoney(g.drawdown, currency)}
          </p>
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
            {t.tradingAccounts.axisDrawdown}
          </p>
        </div>
        <div className="min-w-0 text-center">
          <p
            className={`font-mono font-bold ${valueSize} ${
              g.isPositive ? "text-pnlPositive" : "text-pnlNegative"
            }`}
          >
            {formatMoney(g.balance, currency)}
          </p>
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
            {t.tradingAccounts.axisBalance}
          </p>
        </div>
        <div className="min-w-0 text-right">
          <p className={`font-mono font-semibold ${valueSize}`}>
            {formatMoney(g.remaining, currency)}
          </p>
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
            {t.tradingAccounts.axisRemaining}
          </p>
        </div>
      </div>
    </div>
  );
}
